/**
 * DM-7976: the Charity Verification decision form's logic — the draft the
 * form edits, why it cannot be submitted yet, the request body it becomes,
 * and what the reviewer is told about the route's 409/422 answers. Pure, so
 * it is tested without rendering the form (see charityVerification.logic.ts).
 */

import {
  ContactMethod,
  Decision,
  DecisionErrorCode,
  DocumentDecision,
  IndependentSourceType,
  LetterKind,
  RejectionReasonCode,
  RubricResult,
  statusChip,
  type Option,
} from './charityVerification.logic';
import {
  AdminOpsHttpError,
  type CharityContactConfirmationMethod,
  type CharityDecisionContact,
  type CharityDocumentDecision,
  type CharityDocumentDecisionWire,
  type CharityIndependentSourceType,
  type CharityLetterKind,
  type CharityRejectionReasonCode,
  type CharityRubricGate,
  type CharityRubricItem,
  type CharityRubricResult,
  type CharityVerificationDecided,
  type CharityVerificationDecision,
  type CharityVerificationDecisionPayload,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui';

export const NOTE_MIN_LENGTH = 1;
export const NOTE_MAX_LENGTH = 1000;

export const DECISION_OPTIONS: readonly (Option<CharityVerificationDecision> & { hint: string })[] =
  [
    {
      value: Decision.APPROVE,
      label: 'Approve',
      hint: 'Verifies the organization and promotes its screened draft profile. Needs a passing BMF gate and an independent source.',
    },
    {
      value: Decision.REJECT,
      label: 'Reject',
      hint: 'Closes the claim with a reason code. The claimant may resubmit within 14 days unless the organization is not eligible.',
    },
    {
      value: Decision.REQUEST_CONFIRMATION,
      label: 'Request confirmation',
      hint: 'Emails a 6-digit code to an independently sourced contact, or records a phone call or letter of authorization.',
    },
    {
      value: Decision.REVOKE,
      label: 'Revoke',
      hint: 'Withdraws a verified status and tells the organization’s admins and opted-in followers.',
    },
    {
      value: Decision.DISMISS_DISPUTE,
      label: 'Dismiss dispute',
      hint: 'Closes the contact’s “this wasn’t us” report as unfounded and lifts the block on approval. The claim’s status does not change.',
    },
    {
      value: Decision.CONFIRM_SAMPLE,
      label: 'Confirm sampled approval',
      hint: 'Records that you reviewed this automated approval and agree with it. To disagree, revoke instead.',
    },
  ];

/**
 * Case decisions change no claim status and apply only to some claims (an
 * open dispute, a sampled automated approval awaiting review), so they are
 * shown only when the claim's allowed_actions lists them; the four status
 * decisions are always shown and disabled when not allowed.
 */
export const CASE_DECISIONS: readonly CharityVerificationDecision[] = [
  Decision.DISMISS_DISPUTE,
  Decision.CONFIRM_SAMPLE,
];

export function visibleDecisionOptions(allowedActions: readonly CharityVerificationDecision[]) {
  return DECISION_OPTIONS.filter(
    option => !CASE_DECISIONS.includes(option.value) || allowedActions.includes(option.value)
  );
}

/** A dispute dismissal records only its note: no rubric, documents, letter kind or TEOS link. */
export function decisionRecordsEvidence(decision: CharityVerificationDecision | ''): boolean {
  return decision !== Decision.DISMISS_DISPUTE;
}

export function decisionLabel(decision: string): string {
  return DECISION_OPTIONS.find(option => option.value === decision)?.label ?? decision;
}

export const REJECTION_REASON_OPTIONS: readonly Option<CharityRejectionReasonCode>[] = [
  { value: RejectionReasonCode.DOCUMENT_NOT_VERIFIED, label: 'Document could not be verified' },
  { value: RejectionReasonCode.DOCUMENT_INVALID, label: 'Document is invalid' },
  {
    value: RejectionReasonCode.NOT_ELIGIBLE,
    label: 'Organization is not eligible (no resubmission)',
  },
  {
    value: RejectionReasonCode.CONNECTION_NOT_CONFIRMED,
    label: 'Connection to the organization not confirmed',
  },
];

export const GATE_RESULT_OPTIONS: readonly Option<CharityRubricGate>[] = [
  { value: RubricResult.PASS, label: 'Pass' },
  { value: RubricResult.FAIL, label: 'Fail' },
];

export const RUBRIC_RESULT_OPTIONS: readonly Option<CharityRubricResult>[] = [
  ...GATE_RESULT_OPTIONS,
  { value: RubricResult.NOT_APPLICABLE, label: 'Not applicable' },
];

export const INDEPENDENT_SOURCE_OPTIONS: readonly Option<CharityIndependentSourceType>[] = [
  { value: IndependentSourceType.FORM_990, label: 'Form 990' },
  { value: IndependentSourceType.FORM_990N, label: 'Form 990-N' },
  { value: IndependentSourceType.STATE_REGISTRY, label: 'State charity registry' },
  { value: IndependentSourceType.OTHER, label: 'Other' },
];

export const CONTACT_METHOD_OPTIONS: readonly Option<CharityContactConfirmationMethod>[] = [
  { value: ContactMethod.CODE, label: 'Email a 6-digit code' },
  { value: ContactMethod.PHONE, label: 'Phone call (record it)' },
  { value: ContactMethod.LETTER_OF_AUTHORIZATION, label: 'Letter of authorization (record it)' },
];

export const LETTER_KIND_OPTIONS: readonly Option<CharityLetterKind>[] = [
  { value: LetterKind.DETERMINATION_LETTER, label: 'Determination letter' },
  { value: LetterKind.FORM_1023EZ_DETERMINATION, label: 'Form 1023-EZ determination' },
  { value: LetterKind.AFFIRMATION_LETTER, label: 'Affirmation letter' },
  { value: LetterKind.EIN_LETTER, label: 'EIN letter' },
  { value: LetterKind.GROUP_EXEMPTION_LETTER, label: 'Group exemption letter' },
  { value: LetterKind.PARENT_CONFIRMATION, label: 'Parent confirmation' },
  { value: LetterKind.NOT_A_LETTER, label: 'Not a letter' },
  { value: LetterKind.UNREADABLE, label: 'Unreadable' },
];

export const DOCUMENT_DECISION_OPTIONS: readonly Option<CharityDocumentDecision>[] = [
  { value: DocumentDecision.ACCEPTED, label: 'Accept' },
  { value: DocumentDecision.REJECTED, label: 'Reject' },
];

export interface DocumentDecisionDraft {
  readonly status: CharityDocumentDecision | '';
  readonly rejectionReason: string;
}

export const EMPTY_DOCUMENT_DRAFT: DocumentDecisionDraft = { status: '', rejectionReason: '' };

/** Raw form values; '' means the reviewer has not chosen. */
export interface DecisionDraft {
  readonly decision: CharityVerificationDecision | '';
  readonly reasonCode: CharityRejectionReasonCode | '';
  readonly note: string;
  readonly bmfGate: CharityRubricGate | '';
  readonly letterCheck: CharityRubricResult | '';
  readonly letterCheckNotes: string;
  readonly independentSourceType: CharityIndependentSourceType | '';
  readonly independentSourceValue: string;
  readonly matchesEmailDomain: boolean;
  readonly sanctions: CharityRubricResult | '';
  readonly sanctionsNotes: string;
  readonly nameComparison: CharityRubricGate | '';
  readonly nameComparisonNotes: string;
  /** Keyed by document id; a document with no entry is left as it is. */
  readonly documents: Readonly<Record<string, DocumentDecisionDraft>>;
  readonly letterKind: CharityLetterKind | '';
  readonly teosUrl: string;
  readonly resubmitAllowed: boolean;
  readonly contactMethod: CharityContactConfirmationMethod | '';
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly contactNote: string;
}

export const EMPTY_DECISION_DRAFT: DecisionDraft = {
  decision: '',
  reasonCode: '',
  note: '',
  bmfGate: '',
  letterCheck: '',
  letterCheckNotes: '',
  independentSourceType: '',
  independentSourceValue: '',
  matchesEmailDomain: false,
  sanctions: '',
  sanctionsNotes: '',
  nameComparison: '',
  nameComparisonNotes: '',
  documents: {},
  letterKind: '',
  teosUrl: '',
  resubmitAllowed: true,
  contactMethod: '',
  contactEmail: '',
  contactPhone: '',
  contactNote: '',
};

/** What the page knows about the claim that the draft alone does not. */
export interface DecisionContext {
  readonly allowedActions: readonly CharityVerificationDecision[];
  /** The organization's contact confirmed (by code, phone or letter). */
  readonly contactConfirmed: boolean;
}

/** Why the draft cannot be submitted yet, in the words shown under the form. */
export const DraftProblem = {
  NO_DECISION: 'Choose a decision.',
  DECISION_NOT_ALLOWED: 'That decision is not allowed in the claim’s current status.',
  NOTE_REQUIRED: `A note is required (${NOTE_MIN_LENGTH}–${NOTE_MAX_LENGTH} characters).`,
  REASON_CODE_REQUIRED: 'A rejection needs a reason code.',
  RUBRIC_INCOMPLETE:
    'Record every rubric item: BMF gate, letter check, sanctions screening and name comparison.',
  INDEPENDENT_SOURCE_VALUE_REQUIRED: 'Name the independent source (a URL or a registry reference).',
  BMF_GATE_FAILED: 'An approval needs a passing BMF gate.',
  INDEPENDENT_SOURCE_REQUIRED:
    'An approval needs an independent source that matches the claimant’s email domain, or a confirmed contact.',
  TEOS_URL_INVALID: 'The TEOS link must be an https URL.',
  DOCUMENT_REJECTION_REASON_REQUIRED: 'Give a reason for every rejected document.',
  CONTACT_METHOD_REQUIRED: 'Requesting confirmation needs a contact method.',
  CONTACT_EMAIL_REQUIRED: 'Enter the email address the code goes to.',
  CONTACT_PHONE_REQUIRED: 'Enter the phone number that was called.',
  CONTACT_NOTE_REQUIRED: 'Describe the letter of authorization.',
} as const;
export type DraftProblem = (typeof DraftProblem)[keyof typeof DraftProblem];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTPS_SCHEME = 'https:';

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === HTTPS_SCHEME;
  } catch {
    return false;
  }
}

/** NOT_ELIGIBLE never allows a resubmission, whatever the toggle says. */
export function resubmitAllowedFor(draft: Pick<DecisionDraft, 'reasonCode' | 'resubmitAllowed'>) {
  return draft.reasonCode === RejectionReasonCode.NOT_ELIGIBLE ? false : draft.resubmitAllowed;
}

export function isResubmitLocked(reasonCode: CharityRejectionReasonCode | ''): boolean {
  return reasonCode === RejectionReasonCode.NOT_ELIGIBLE;
}

function rubricProblems(draft: DecisionDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];
  const complete =
    draft.bmfGate !== '' &&
    draft.letterCheck !== '' &&
    draft.sanctions !== '' &&
    draft.nameComparison !== '';
  if (!complete) problems.push(DraftProblem.RUBRIC_INCOMPLETE);
  if (draft.independentSourceType !== '' && draft.independentSourceValue.trim() === '') {
    problems.push(DraftProblem.INDEPENDENT_SOURCE_VALUE_REQUIRED);
  }
  return problems;
}

/**
 * Mirrors the route's APPROVE rules so the control disables for the reasons
 * the API would answer 422: a passing BMF gate, and an independent source
 * that matches the email domain unless the contact has confirmed.
 */
function approveProblems(draft: DecisionDraft, context: DecisionContext): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (draft.bmfGate === RubricResult.FAIL) problems.push(DraftProblem.BMF_GATE_FAILED);
  const sourceOk =
    draft.independentSourceType !== '' &&
    draft.independentSourceValue.trim() !== '' &&
    (draft.matchesEmailDomain || context.contactConfirmed);
  if (!sourceOk) problems.push(DraftProblem.INDEPENDENT_SOURCE_REQUIRED);
  return problems;
}

function contactProblems(draft: DecisionDraft): DraftProblem[] {
  switch (draft.contactMethod) {
    case ContactMethod.CODE:
      return EMAIL_PATTERN.test(draft.contactEmail.trim())
        ? []
        : [DraftProblem.CONTACT_EMAIL_REQUIRED];
    case ContactMethod.PHONE:
      return draft.contactPhone.trim() === '' ? [DraftProblem.CONTACT_PHONE_REQUIRED] : [];
    case ContactMethod.LETTER_OF_AUTHORIZATION:
      return draft.contactNote.trim() === '' ? [DraftProblem.CONTACT_NOTE_REQUIRED] : [];
    default:
      return [DraftProblem.CONTACT_METHOD_REQUIRED];
  }
}

function documentProblems(draft: DecisionDraft): DraftProblem[] {
  const missingReason = Object.values(draft.documents).some(
    entry => entry.status === DocumentDecision.REJECTED && entry.rejectionReason.trim() === ''
  );
  return missingReason ? [DraftProblem.DOCUMENT_REJECTION_REASON_REQUIRED] : [];
}

/** Everything stopping the draft from being submitted; empty means it can be. */
export function decisionProblems(draft: DecisionDraft, context: DecisionContext): DraftProblem[] {
  if (draft.decision === '') return [DraftProblem.NO_DECISION];
  const problems: DraftProblem[] = [];
  if (!context.allowedActions.includes(draft.decision)) {
    problems.push(DraftProblem.DECISION_NOT_ALLOWED);
  }
  const note = draft.note.trim();
  if (note.length < NOTE_MIN_LENGTH || note.length > NOTE_MAX_LENGTH) {
    problems.push(DraftProblem.NOTE_REQUIRED);
  }
  if (draft.decision === Decision.REJECT && draft.reasonCode === '') {
    problems.push(DraftProblem.REASON_CODE_REQUIRED);
  }
  if (!decisionRecordsEvidence(draft.decision)) return problems;
  problems.push(...rubricProblems(draft));
  if (draft.decision === Decision.APPROVE) problems.push(...approveProblems(draft, context));
  if (draft.teosUrl.trim() !== '' && !isHttpsUrl(draft.teosUrl.trim())) {
    problems.push(DraftProblem.TEOS_URL_INVALID);
  }
  problems.push(...documentProblems(draft));
  if (draft.decision === Decision.REQUEST_CONFIRMATION) problems.push(...contactProblems(draft));
  return problems;
}

function rubricItem(result: CharityRubricResult | '', notes: string): CharityRubricItem {
  const trimmed = notes.trim();
  return {
    result: result === '' ? RubricResult.NOT_APPLICABLE : result,
    ...(trimmed === '' ? {} : { notes: trimmed }),
  };
}

function documentDecisions(draft: DecisionDraft): CharityDocumentDecisionWire[] {
  const decisions: CharityDocumentDecisionWire[] = [];
  for (const [documentId, entry] of Object.entries(draft.documents)) {
    if (entry.status === '') continue;
    const reason = entry.rejectionReason.trim();
    decisions.push({
      document_id: documentId,
      status: entry.status,
      ...(entry.status === DocumentDecision.REJECTED && reason !== ''
        ? { rejection_reason: reason }
        : {}),
    });
  }
  return decisions;
}

function contactPayload(draft: DecisionDraft): CharityDecisionContact | undefined {
  const note = draft.contactNote.trim();
  switch (draft.contactMethod) {
    case ContactMethod.CODE:
      return { method: ContactMethod.CODE, email: draft.contactEmail.trim() };
    case ContactMethod.PHONE:
      return {
        method: ContactMethod.PHONE,
        phone: draft.contactPhone.trim(),
        ...(note === '' ? {} : { note }),
      };
    case ContactMethod.LETTER_OF_AUTHORIZATION:
      return { method: ContactMethod.LETTER_OF_AUTHORIZATION, note };
    default:
      return undefined;
  }
}

/**
 * The request body for a draft that passed decisionProblems. Optional keys
 * are omitted rather than sent null, and reason_code is always present —
 * null unless rejecting — exactly as the route reads it.
 */
export function buildDecisionPayload(draft: DecisionDraft): CharityVerificationDecisionPayload {
  if (draft.decision === '') throw new Error(DraftProblem.NO_DECISION);
  if (!decisionRecordsEvidence(draft.decision)) {
    return { decision: draft.decision, reason_code: null, note: draft.note.trim() };
  }
  const documents = documentDecisions(draft);
  const teosUrl = draft.teosUrl.trim();
  const contact =
    draft.decision === Decision.REQUEST_CONFIRMATION ? contactPayload(draft) : undefined;
  return {
    decision: draft.decision,
    reason_code:
      draft.decision === Decision.REJECT && draft.reasonCode !== '' ? draft.reasonCode : null,
    note: draft.note.trim(),
    rubric: {
      bmf_gate: draft.bmfGate === '' ? RubricResult.FAIL : draft.bmfGate,
      letter_check: rubricItem(draft.letterCheck, draft.letterCheckNotes),
      independent_source:
        draft.independentSourceType === ''
          ? null
          : {
              type: draft.independentSourceType,
              value: draft.independentSourceValue.trim(),
              matches_email_domain: draft.matchesEmailDomain,
            },
      sanctions: rubricItem(draft.sanctions, draft.sanctionsNotes),
      name_comparison: {
        result: draft.nameComparison === '' ? RubricResult.FAIL : draft.nameComparison,
        ...(draft.nameComparisonNotes.trim() === ''
          ? {}
          : { notes: draft.nameComparisonNotes.trim() }),
      },
    },
    ...(documents.length > 0 ? { documents } : {}),
    ...(draft.letterKind === '' ? {} : { letter_kind: draft.letterKind }),
    ...(teosUrl === '' ? {} : { teos_url: teosUrl }),
    ...(draft.decision === Decision.REJECT ? { resubmit_allowed: resubmitAllowedFor(draft) } : {}),
    ...(contact === undefined ? {} : { contact }),
  };
}

// ---------------------------------------------------------------------------
// Result and error handling
// ---------------------------------------------------------------------------

export interface DecisionFeedback {
  readonly tone: ChipTone;
  readonly title: string;
  readonly message: string;
  /** From a 409 CLAIM_NOT_IN_STATE: what the claim now allows. */
  readonly allowedActions: readonly CharityVerificationDecision[];
  /** The claim moved under the reviewer; the detail must be read again. */
  readonly refreshClaim: boolean;
}

export const PENDING_SECOND_OPERATOR_FEEDBACK: DecisionFeedback = {
  tone: 'warn',
  title: 'Waiting for a second operator',
  message:
    'Your approval is recorded. A different operator must approve this claim before the organization is verified; you cannot record the second approval yourself.',
  allowedActions: [],
  refreshClaim: false,
};

function readAllowedActions(details: unknown): CharityVerificationDecision[] {
  if (typeof details !== 'object' || details === null) return [];
  const actions = (details as { allowed_actions?: unknown }).allowed_actions;
  return Array.isArray(actions)
    ? actions.filter((action): action is CharityVerificationDecision => typeof action === 'string')
    : [];
}

function readStatus(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;
  const status = (details as { status?: unknown }).status;
  return typeof status === 'string' ? status : null;
}

const DECISION_ERROR_FEEDBACK: Record<
  Exclude<DecisionErrorCode, 'CLAIM_NOT_IN_STATE'>,
  Pick<DecisionFeedback, 'title' | 'message'>
> = {
  SECOND_OPERATOR_REQUIRED: {
    title: 'A different operator must approve',
    message:
      'You recorded the first approval of this claim. The second one has to come from another operator.',
  },
  CLAIM_DISPUTED: {
    title: 'Claim disputed',
    message:
      'The organization reported that this claim was not made by them. Resolve the dispute before approving.',
  },
  INDEPENDENT_SOURCE_REQUIRED: {
    title: 'Independent source required',
    message:
      'An approval needs an independent source that matches the claimant’s email domain, or a confirmed contact. Record one in the rubric.',
  },
  BMF_GATE_FAILED: {
    title: 'BMF gate failed',
    message: 'The organization does not pass the BMF gate, so it cannot be approved.',
  },
};

/**
 * What the reviewer is told about a failed decision, driven by the route's
 * problem+json code rather than its message text. A CLAIM_NOT_IN_STATE answer
 * names the claim's current status and allowed actions and asks for a refresh.
 */
export function decisionFeedback(error: unknown): DecisionFeedback {
  const code = error instanceof AdminOpsHttpError ? error.code : undefined;
  if (error instanceof AdminOpsHttpError && code === DecisionErrorCode.CLAIM_NOT_IN_STATE) {
    const status = readStatus(error.details);
    const allowedActions = readAllowedActions(error.details);
    const allowed =
      allowedActions.length === 0
        ? 'no decision is allowed'
        : `allowed: ${allowedActions.map(decisionLabel).join(', ')}`;
    return {
      tone: 'warn',
      title: 'Claim moved on',
      message: `${status === null ? 'The claim changed state' : `The claim is now ${statusChip(status).label}`}; ${allowed}. The claim has been reloaded.`,
      allowedActions,
      refreshClaim: true,
    };
  }
  const known =
    code !== undefined && code in DECISION_ERROR_FEEDBACK
      ? DECISION_ERROR_FEEDBACK[code as Exclude<DecisionErrorCode, 'CLAIM_NOT_IN_STATE'>]
      : undefined;
  if (known !== undefined) {
    return { tone: 'crit', ...known, allowedActions: [], refreshClaim: false };
  }
  return {
    tone: 'crit',
    title: 'Decision not recorded',
    message: error instanceof Error ? error.message : 'Unknown error',
    allowedActions: [],
    refreshClaim: false,
  };
}

/** The toast after a recorded decision; names the wait when one was parked. */
export function decisionSuccessMessage(
  result: Pick<CharityVerificationDecided, 'pending_second_operator' | 'notifications_queued'>,
  decision: CharityVerificationDecision
): string {
  if (result.pending_second_operator) {
    return 'Approval recorded — a second operator must approve this claim';
  }
  const queued = result.notifications_queued.length;
  return `${decisionLabel(decision)} recorded${queued > 0 ? ` (${queued} notification${queued === 1 ? '' : 's'} queued)` : ''}`;
}
